import type { Skill, StageInfo } from './types.ts';

// Reward-based training only. Short sessions (2–5 minutes), lots of success,
// stop while he's still keen. Training treats come out of his daily ration.

export const STAGES: StageInfo[] = [
  {
    id: 1,
    title: "Foundations",
    ages: "8–10 weeks",
    focus: "Help him feel safe, learn the marker word, and build good habits around toilet, crate, sleep and handling from day one.",
  },
  {
    id: 2,
    title: "Building blocks",
    ages: "10–16 weeks",
    focus: "Add the core cues at home and on quiet outings while the socialisation window is still wide open.",
  },
  {
    id: 3,
    title: "Out and about",
    ages: "4–6 months",
    focus: "Take the skills outside, with a long line and lots of rewards, so they work around real-world distractions.",
  },
  {
    id: 4,
    title: "Teenager proofing",
    ages: "6–12 months",
    focus: "Keep going through adolescence: proof recall and self-control, and be patient when he seems to forget everything.",
  },
];

export const SKILL_LEVELS: { level: 0 | 1 | 2 | 3 | 4; label: string; detail: string }[] = [
  { level: 0, label: "Not started", detail: "We haven’t begun this one yet." },
  { level: 1, label: "Introduced", detail: "He’s seen it a few times and is starting to work out the game." },
  { level: 2, label: "Getting it", detail: "Works at home with few distractions, most of the time." },
  { level: 3, label: "Reliable at home", detail: "Responds first time, at home and at his quiet toilet spot, for either of us." },
  { level: 4, label: "Reliable anywhere", detail: "Works out on walks, around other dogs, people and smells." },
];

export const SKILLS: Skill[] = [
  // --- Stage 1: Foundations -------------------------------------------------
  {
    id: 'marker',
    name: "Marker word",
    stage: 1,
    fromWeeks: 8,
    cueId: 'marker',
    summary: "Teach him that “Yes!” means a treat is on its way.",
    why: "The marker lets you tell him the exact moment he got it right. It makes every other skill quicker to teach, and both of you can use it the same way.",
    steps: [
      "Sit with 10 tiny treats somewhere quiet.",
      "Say “Yes!” once, then give a treat within a second. He doesn’t have to do anything yet.",
      "Repeat 10 times, then stop. Do two or three short sessions a day for a couple of days.",
      "Test it: when he’s looking away, say “Yes!”. If his head whips round to you, it’s charged.",
      "From now on, say “Yes!” the instant he does the right thing, then reward.",
    ],
    tips: [
      "Every “Yes!” is followed by a treat, even if you said it by mistake.",
      "Keep your hands still while you say it, then move to the treat, so the word (not your hand) is the signal.",
    ],
  },
  {
    id: 'name-game',
    name: "Name game",
    stage: 1,
    fromWeeks: 8,
    cueId: 'name',
    summary: "His name means “look at me, something good is coming”.",
    why: "A quick head-turn to his name is the start of recall and of getting his attention anywhere. It only works if his name always predicts good things.",
    steps: [
      "Wait until he’s nearby and not busy. Say “Arlo” once in a happy voice.",
      "The moment he looks at you, say “Yes!” and give a treat.",
      "Repeat 5–10 times, a few times a day, in different rooms.",
      "Gradually try it when he’s a bit more distracted, or from further away.",
      "Both of you play it, so he responds to either voice.",
    ],
    tips: [
      "Don’t say his name over and over if he doesn’t look. Make it easier instead (closer, fewer distractions).",
      "Never use his name to scold him.",
    ],
  },
  {
    id: 'toilet-cue',
    name: "Toilet on cue",
    stage: 1,
    fromWeeks: 8,
    cueId: 'toilet',
    summary: "Go outside, go on cue, get rewarded.",
    why: "Taking him out often and rewarding him outside is the whole of toilet training. Adding a cue word means he’ll eventually go when you ask, which is priceless on a rainy night.",
    steps: [
      "Carry him down to the same spot outside: on waking, after meals, after play, and every hour while he’s awake.",
      "Stand still and be boring. Let him sniff.",
      "As he starts to go, say “Be quick” quietly once.",
      "When he finishes, say “Yes!” and give a treat right there, then have a little play.",
      "Log it on the tablet or tap the NFC tag, so you both know when he last went.",
    ],
    tips: [
      "Accidents indoors: clean with an enzyme cleaner and say nothing. Never punish; just take him out more often.",
      "Night trips: lead on, straight out, straight back to bed. No play, no chat.",
    ],
  },
  {
    id: 'crate-love',
    name: "Crate love",
    stage: 1,
    fromWeeks: 8,
    summary: "The crate is his safe den, never a punishment.",
    why: "Puppies who slept in a crate or closed room overnight had fewer separation problems at six months (Generation Pup). A happy crate also makes vet stays, car travel and holidays easier.",
    steps: [
      "Set it up with a soft bed and a cover over three sides, near where you are. Door open.",
      "Drop treats inside for him to find. Feed meals in there.",
      "Give chews or a stuffed Kong only in the crate, with the door open at first.",
      "When he’s happily going in, close the door for a few seconds while he eats, then open it before he asks to come out.",
      "Build up slowly. Stay close by at first, especially at night.",
    ],
    tips: [
      "Never shut him in to cry it out. If he’s upset, go back a step.",
      "At night, have the crate by your bed for the first weeks so you can reassure him and hear when he needs a wee.",
    ],
  },
  {
    id: 'settle',
    name: "Settle",
    stage: 1,
    fromWeeks: 8,
    cueId: 'settle',
    summary: "Learn to lie down and switch off on a mat.",
    why: "Puppies need 18–20 hours of sleep and many can’t switch themselves off. A puppy who can settle bites less, has fewer zoomies and copes far better with being left.",
    steps: [
      "Sit on the sofa with him on the lead and his mat on the floor. Have a chew or a book for you.",
      "Ignore him. Each time he lies down or relaxes, quietly drop a treat between his paws.",
      "When he’s lying there calmly, say “Settle” softly and keep rewarding calm.",
      "Do 5–10 minutes after a walk or play, when he’s already a bit tired.",
      "Gradually take the mat to other rooms, then later to a café or friend’s house.",
    ],
    tips: [
      "After 45–60 minutes awake, most puppies need a nap. Pop him in his crate with a chew rather than waiting for the zoomies.",
    ],
  },
  {
    id: 'handling',
    name: "Happy handling",
    stage: 1,
    fromWeeks: 8,
    summary: "Being touched on ears, paws, mouth and tail predicts treats.",
    why: "He’ll be brushed, checked, dried and examined for the rest of his life. Pairing touch with treats now makes vet and grooming visits calm instead of a wrestle.",
    steps: [
      "Touch one ear briefly, say “Yes!”, treat. Then let go.",
      "Repeat with the other ear, each paw, lifting his lip, feeling his tummy and tail.",
      "Build up to holding each bit for a few seconds, then peeking inside ears and touching nails.",
      "Add the tools one at a time: brush, comb, towel, nail clippers (just touching, no clipping).",
      "Keep sessions to a minute or two, and stop if he pulls away. Go back a step next time.",
    ],
    tips: [
      "Try a lick mat smeared with a bit of his wet food to keep him busy while you handle.",
      "Let him opt out: if he moves away, pause. A dog who can say “not now” is less likely to need to growl.",
    ],
    cocker: "Cocker ears need checking 1–2 times a week and drying after baths and swims, and the breed has a high rate of ear and dental problems. Those long feathery ears and feet also mean grooming for life, so this is one of the most important things you’ll teach him.",
  },
  {
    id: 'swap',
    name: "Swap and trade",
    stage: 1,
    fromWeeks: 8,
    cueId: 'swap',
    summary: "Letting go of things always leads to something better.",
    why: "Puppies pick up everything. If we snatch things away, they learn to run off, gulp or guard. If we trade, they learn that people near their stuff is great news.",
    steps: [
      "While he’s chewing a low-value toy, show him a tasty treat at his nose and say “Swap”.",
      "When he lets go, say “Yes!”, give the treat, then give the toy back. Most of the time he gets his thing back.",
      "At the food bowl: walk past and drop in something tastier (“add, don’t take”). Never take the bowl away while he’s eating.",
      "Practise with more exciting items as he gets good at it.",
      "If he has something dangerous, trade with the best thing you have, even a handful of food on the floor.",
    ],
    tips: [
      "If he freezes, stiffens, eats faster or growls over something, back off calmly and trade from a distance. Tell your vet or a qualified behaviourist.",
      "Don’t let visitors or children take things from him.",
    ],
    cocker: "Resource guarding is a known breed tendency in cockers, most reported in solid-colour (especially red and golden) males like Arlo. Prevention from day one is simple: trade up, “add, don’t take” at the bowl, and never snatch.",
  },
  {
    id: 'gentle',
    name: "Gentle mouth",
    stage: 1,
    fromWeeks: 8,
    cueId: 'gentle',
    summary: "Take treats softly, and learn that teeth on skin ends the fun.",
    why: "All puppies nip; it’s how they explore. Teaching a soft mouth now (bite inhibition) means that as an adult his first reaction is gentle, even in a fright.",
    steps: [
      "Hold a treat in a closed fist. Let him lick and nibble. The instant he backs off or softens, say “Yes!” and open your hand.",
      "Add the word “Gentle” once he’s doing it reliably.",
      "In play: if teeth touch skin, say “Oops” calmly, stop and turn away for a few seconds, then carry on.",
      "Redirect nibbling on to a tug or chew toy. Keep one in your pocket.",
      "If he gets bitey and wild, he’s probably overtired. Pop him in his crate with a chew for a nap.",
    ],
    tips: [
      "No tapping his nose, holding his mouth shut or yelping loudly; these often make it worse.",
    ],
    cocker: "Cockers are soft-mouthed gundogs by design, which makes this easier than for many breeds. Carrying a toy often calms a mouthy cocker.",
  },
  {
    id: 'sit',
    name: "Sit",
    stage: 1,
    fromWeeks: 8,
    cueId: 'sit',
    summary: "Bottom on the floor, and stay there until released.",
    why: "Sit is the easy ‘please’ for everything: before his dinner, before the door opens, before saying hello. It gives him something to do instead of jumping.",
    steps: [
      "Hold a treat at his nose and slowly lift it up and slightly back over his head.",
      "As his bottom touches the floor, say “Yes!” and give the treat.",
      "After a few goes, do the same hand movement without a treat in it, and reward from the other hand.",
      "Once he’s sitting as your hand moves, say “Sit” just before the hand signal.",
      "End each sit with “Free”, so he learns to hold it until released.",
    ],
    tips: [
      "Reward in position. Give the treat while he’s still sitting, not after he’s stood up.",
    ],
  },
  {
    id: 'hand-touch',
    name: "Hand touch",
    stage: 1,
    fromWeeks: 9,
    cueId: 'touch',
    summary: "Nose to palm on cue. A tiny skill with lots of uses.",
    why: "It’s an easy way to move him around without grabbing, a lovely mini-recall, and a useful ‘look at this, not that’ when passing something exciting.",
    steps: [
      "Hold your flat hand a few centimetres from his nose. He’ll sniff it; say “Yes!” and treat from the other hand.",
      "Repeat, moving your hand slightly to the side or lower each time.",
      "Once he’s bumping it eagerly, add “Touch” just before you present your hand.",
      "Gradually move your hand further away so he has to take a step or two.",
      "Use it to move him off the sofa, through a door or away from a distraction.",
    ],
  },

  // --- Stage 2: Building blocks ---------------------------------------------
  {
    id: 'recall',
    name: "Recall",
    stage: 2,
    fromWeeks: 9,
    cueId: 'recall',
    summary: "Come straight back when called, every time.",
    why: "A great recall is the most important safety skill he’ll have, and the key to off-lead fun later. It is built from hundreds of easy wins while he’s young and still wants to be near you.",
    steps: [
      "At home, when he’s already heading your way, say “Arlo, come!” happily and reward with something extra tasty.",
      "Play ‘puppy ping-pong’: one of you at each end of a room or hallway, taking turns to call him.",
      "Move to the hallway or a quiet outdoor spot on a long line. Call him away from mild distractions, and run backwards to make it a game.",
      "When he arrives, touch his collar gently before giving the treat, so being caught is part of the deal.",
      "Mostly let him go back to what he was doing afterwards. Recall shouldn’t always mean the fun ends.",
    ],
    tips: [
      "Only call when you’re fairly sure he’ll come. If he won’t, go and get him and make it easier next time.",
      "Never tell him off when he arrives, no matter how long it took.",
    ],
  },
  {
    id: 'alone-time',
    name: "Alone time",
    stage: 2,
    fromWeeks: 9,
    summary: "Build up being on his own gradually, from seconds to hours.",
    why: "Nearly half of puppies in the Generation Pup study showed some separation-related behaviour by six months. Practising short, calm absences early is one of the best things you can do to prevent it.",
    steps: [
      "Start with a baby gate: give him a chew on one side and potter about on the other where he can see you.",
      "Step out of sight for a few seconds, then come back calmly. No big hello.",
      "Build up slowly: 10 seconds, 30 seconds, 1 minute, 5 minutes, only increasing when he stays relaxed.",
      "Practise leaving the house the same way: keys, shoes, coat, out, back in again before he gets worried.",
      "Watch him on the camera. If he cries, paces or scratches at the door, you went too fast; go back a step.",
    ],
    tips: [
      "Do it after a walk, a wee and some play, when he’s ready to nap.",
      "Coming home: keep greetings calm, even if something’s been chewed. Fussing on return after misbehaviour made problems about six times more likely.",
      "RSPCA guidance: puppies much less than the 4-hour adult maximum.",
    ],
  },
  {
    id: 'down',
    name: "Down",
    stage: 2,
    fromWeeks: 10,
    cueId: 'down',
    summary: "Lie down with elbows on the floor.",
    why: "Down is a calm, stable position. It’s the basis of settle, stay and relaxing in a pub or at the vet.",
    steps: [
      "From a sit, hold a treat at his nose and slowly lower it straight down between his paws, then slightly away.",
      "The instant his elbows touch the floor, say “Yes!” and treat on the floor between his paws.",
      "If he stands up, reset and try a smaller movement. Sitting on the floor with your leg as a ‘tunnel’ can help.",
      "Fade the treat from your hand, then add “Down” just before the hand signal.",
      "Release with “Free”.",
    ],
    tips: [
      "“Down” only ever means lie down. Use “Off” for jumping up or furniture.",
    ],
  },
  {
    id: 'leave-it',
    name: "Leave it",
    stage: 2,
    fromWeeks: 10,
    cueId: 'leave',
    summary: "Look away from something tempting and back to you.",
    why: "Puppies eat everything, and some of it is dangerous. A strong “Leave it” can save a vet trip, or his life.",
    steps: [
      "Hold a treat in a closed fist. Let him sniff and paw. The moment he pulls back, say “Yes!” and reward from your other hand.",
      "When he’s backing off straight away, add “Leave it” as you present your fist.",
      "Put the treat on the floor under your hand or foot. Same game: reward him for looking away.",
      "Move on to treats on the floor uncovered (be ready to cover them), then dropped food, then toys.",
      "Practise on walks with planted treats, on the lead, before you need it for real.",
    ],
    tips: [
      "He never gets the thing he left. The reward always comes from you, and it’s better.",
    ],
    cocker: "Cockers are world-class scavengers. Lungworm comes from slugs, snails and puddles, and they’re also partial to dead things, poo and dropped chicken bones, so this one really matters.",
  },
  {
    id: 'wait',
    name: "Wait",
    stage: 2,
    fromWeeks: 10,
    cueId: 'wait',
    summary: "Pause at doors, gates, the car and the food bowl until released.",
    why: "Doors and car boots are where puppies bolt. A habit of pausing until he hears “Free” keeps him safe on busy roads.",
    steps: [
      "With him on the lead at an inside door, put your hand on the handle. If he stays still, say “Yes!” and treat.",
      "Open the door a crack. If he moves towards it, close it gently. If he holds still, “Yes!” and treat.",
      "Build up until the door is wide open and he waits. Say “Free” to let him through.",
      "Add “Wait” once he understands the game.",
      "Use it at the flat door, the stair door, the lift, the car boot and before putting his bowl down.",
    ],
  },
  {
    id: 'four-on-floor',
    name: "Four on the floor",
    stage: 2,
    fromWeeks: 10,
    cueId: 'off',
    summary: "Jumping up gets nothing; four paws on the floor gets attention.",
    why: "A 3 kg puppy jumping up is cute; a 16 kg muddy spaniel is not, especially with children or older people. It’s much easier to teach before the habit forms.",
    steps: [
      "When he comes to greet you, drop a treat or two on the floor before he has a chance to jump.",
      "If he jumps, stand still, fold your arms and look away. The instant all four feet are down, crouch and fuss him gently.",
      "Keep greetings low and calm: crouch to his level.",
      "Ask visitors to do the same. Keep him on a lead or behind a gate until he’s better at it.",
      "Use “Off” (never “Down”) to ask him to get his feet off something.",
    ],
    tips: [
      "Everyone has to be consistent. One person who lets him jump up keeps the habit alive.",
    ],
  },
  {
    id: 'whistle-recall',
    name: "Whistle recall",
    stage: 2,
    fromWeeks: 10,
    cueId: 'recall',
    summary: "Pip-pip-pip on the whistle means the best food of the day is at your feet.",
    why: "A whistle sounds the same whoever blows it and whatever mood you’re in, and it carries much further than your voice across a field or through woods.",
    steps: [
      "At home, blow pip-pip-pip softly and immediately feed a small handful of something special.",
      "Do this just before some of his meals for a week or so, without expecting anything. He’s learning the sound means food.",
      "Then blow it when he’s a few steps away. He’ll turn and come. Jackpot him.",
      "Practise in the flat and quiet corners outside, then on walks on a long line.",
      "Both of you use the same pattern and the same whistle.",
    ],
    tips: [
      "Don’t blow it unless you can back it up. It should almost never fail.",
    ],
    cocker: "Cockers are gundogs and respond brilliantly to a whistle; they’re bred for it. An ACME 210.5 is the classic gundog whistle, and plenty of cocker owners find it far more reliable than their voice.",
  },
  {
    id: 'stand',
    name: "Stand",
    stage: 2,
    fromWeeks: 11,
    cueId: 'stand',
    summary: "Stand still on all four feet.",
    why: "It makes vet examinations, grooming, brushing his tummy and towel drying so much easier.",
    steps: [
      "From a sit, hold a treat at his nose and move it forward, away from him, at nose height.",
      "As he stands to follow, say “Yes!” and treat while he’s standing.",
      "Build up the time he stands before the treat, a second at a time.",
      "Add “Stand” just before the hand movement.",
      "Practise on the grooming table or a non-slip mat, with gentle handling while he stands.",
    ],
  },
  {
    id: 'car-travel',
    name: "Happy car travel",
    stage: 2,
    fromWeeks: 10,
    summary: "The car is calm, safe and leads to nice places.",
    why: "He’ll need the car for the vet, the groomer and the best walks. The law (Highway Code rule 57) also needs him to be safely restrained.",
    steps: [
      "With the engine off, pop him in his crate or harness restraint in the car, give a treat and a chew, then get him out again.",
      "Next time, sit in the car with the engine running for a minute.",
      "Then short trips around the block, ending somewhere nice or back home with a game.",
      "Build up slowly. Keep early trips short and make sure he hasn’t just eaten.",
      "Lift him in and out while he’s young, rather than letting him jump.",
    ],
    tips: [
      "Car sickness is common in puppies and usually improves. Ask your vet if it doesn’t.",
    ],
  },
  {
    id: 'loose-lead',
    name: "Loose-lead walking",
    stage: 2,
    fromWeeks: 11,
    cueId: 'lets-go',
    summary: "A loose lead means we keep going; a tight lead means we stop.",
    why: "Spaniels love to pull towards smells. Teaching it early, before he’s strong, saves years of sore shoulders and makes walks nicer for all of you.",
    steps: [
      "Start indoors and in the hallway with the lead on. Reward him every time he’s next to your leg.",
      "Say “Let’s go” and walk a few steps. Reward him often by your side.",
      "If the lead goes tight, stop and wait. When it goes slack, “Yes!” and carry on.",
      "Change direction often to keep him watching you.",
      "Once he can go out, keep outings short and mix in plenty of sniffing on a loose lead.",
    ],
    tips: [
      "Use a harness for walks, so pulling doesn’t put pressure on his neck.",
      "Say “Go sniff” to let him explore. Sniffing is a reward in itself.",
    ],
  },

  // --- Stage 3: Out and about -----------------------------------------------
  {
    id: 'scent-games',
    name: "Scent games",
    stage: 3,
    fromWeeks: 12,
    cueId: 'find-it',
    summary: "Nose work to tire out his brain.",
    why: "Sniffing is one of the most tiring and satisfying things a dog can do. Ten minutes of nose work can be worth half an hour of walking, which helps while his joints are still growing.",
    steps: [
      "Toss a treat on the floor and say “Find it” as he goes to eat it.",
      "Scatter a few treats in the grass or a snuffle mat and let him hunt.",
      "Hide treats under upturned cups, then around the room, then around the flat.",
      "Hide a toy with a treat inside and let him search for it.",
      "Later, try a reward-based scentwork class, where he learns to search for one particular smell.",
    ],
    cocker: "Cockers are bred to hunt with their noses, and scent games are proper spaniel enrichment. A cocker who has used his nose is a much calmer cocker.",
  },
  {
    id: 'retrieve',
    name: "Retrieve",
    stage: 3,
    fromWeeks: 12,
    cueId: 'drop',
    summary: "Bring it back to hand and let go on cue.",
    why: "Retrieving is great exercise and a brilliant outlet for a gundog. It also teaches him to bring you things instead of running off with them.",
    steps: [
      "In a hallway or small space, roll a soft toy a short way. When he picks it up, call him back cheerfully.",
      "When he reaches you, don’t grab. Offer a treat and say “Drop”, then throw it again as a reward.",
      "Keep it short: three or four throws, then stop while he’s keen.",
      "Gradually move to bigger spaces and longer throws.",
      "Later, try a small puppy dummy and a sit before you throw.",
    ],
    tips: [
      "Avoid repetitive, high-speed ball throwing while he’s growing. Short gentle retrieves on soft ground are fine.",
    ],
    cocker: "Retrieving is natural for most cockers; it’s what they were bred to do. Many will carry things everywhere and present you with a sock as a gift. Reward it rather than chasing him for it.",
  },
  {
    id: 'long-line-recall',
    name: "Long-line recall",
    stage: 3,
    fromWeeks: 14,
    cueId: 'recall',
    summary: "Practise recall in the real world, with a safety net.",
    why: "A long line lets him have freedom and sniff while you still control what happens. He can’t learn that ignoring you pays off.",
    steps: [
      "Clip a 5–10 m long line to his harness (never to the collar) in a quiet open space.",
      "Let the line trail or hold it loosely. Let him wander and sniff.",
      "Call or whistle when he’s not too distracted. Reward generously when he arrives, then let him go again.",
      "If he doesn’t come, calmly shorten the line and reel him in. Try an easier recall next time.",
      "Slowly work up to more distracting places. Only try off lead when he’s nearly perfect on the line.",
    ],
    tips: [
      "Wear gloves and don’t wrap the line around your hand.",
    ],
  },
  {
    id: 'calm-greetings',
    name: "Calm greetings",
    stage: 3,
    fromWeeks: 14,
    summary: "Say hello to people and dogs politely, or walk past them calmly.",
    why: "Not every dog or person wants to meet a bouncy spaniel. Learning that he doesn’t have to greet everyone prevents frustration and keeps him out of trouble.",
    steps: [
      "On walks, reward him for looking at another dog or person and then back to you.",
      "Mostly walk past without greeting. Only let him say hello to calm people and friendly adult dogs whose owners agree.",
      "Before a greeting, ask for a sit or four on the floor; greet only when he’s calm.",
      "Keep dog-to-dog greetings short (three seconds), then call him away with a treat.",
      "At home, keep him on a lead or behind a gate when visitors arrive until he’s calm.",
    ],
  },
  {
    id: 'stay',
    name: "Stay",
    stage: 3,
    fromWeeks: 16,
    cueId: 'stay',
    summary: "Hold a sit or down while you move away, until released.",
    why: "Stay keeps him safe while you open the car, pick up after him or answer the door, and builds real self-control.",
    steps: [
      "Ask for a sit or down. Say “Stay”, wait one second, “Yes!”, treat in position, then “Free”.",
      "Build the three Ds one at a time: duration (seconds), distance (a step), then distractions.",
      "When you add distance, reduce the time. When you add distractions, go back close.",
      "Always walk back to him to reward. Don’t call him out of a stay while he’s learning.",
      "Practise at home, then on quiet walks.",
    ],
  },
  {
    id: 'place',
    name: "Go to bed",
    stage: 3,
    fromWeeks: 16,
    cueId: 'place',
    summary: "Go to your bed and stay there calmly until released.",
    why: "Brilliant for mealtimes, visitors and when the doorbell goes. It gives him a clear job instead of barking or jumping.",
    steps: [
      "Stand next to his bed. Throw a treat on it. When he steps on, “Yes!”, treat.",
      "Wait for him to step on without a lure, then reward. Then wait for a down on the bed.",
      "Add “On your bed” just before he goes.",
      "Take a step back each time so he goes to the bed from further away.",
      "Add duration with treats, then release with “Free”. Practise when someone knocks.",
    ],
  },

  // --- Stage 4: Teenager proofing -------------------------------------------
  {
    id: 'impulse-control',
    name: "Impulse control",
    stage: 4,
    fromWeeks: 20,
    cueId: 'release',
    summary: "Good things come to dogs who wait.",
    why: "Adolescence (around 6–18 months) makes self-control harder. Games that reward waiting help him think before he acts, rather than chasing, grabbing or bolting.",
    steps: [
      "Hold a treat in your open hand. If he dives in, close it. If he waits, “Yes!” and give it.",
      "Lower his food bowl slowly. If he gets up, lift it. When he waits, put it down and say “Free”.",
      "Before throwing a toy, ask for a sit and a second’s pause.",
      "Play tug with pauses: “Drop”, wait for calm, then “Free” and start again.",
      "Use the same idea for doors, the car and getting his lead on.",
    ],
    tips: [
      "Expect him to seem to forget things around 8 months. It’s normal; go back to easier versions and keep rewarding.",
    ],
  },
  {
    id: 'settle-out',
    name: "Settle out and about",
    stage: 4,
    fromWeeks: 22,
    cueId: 'settle',
    summary: "Lie calmly on his mat in a café, pub garden or friend’s house.",
    why: "Being able to relax in busy places means he can come along to more of your life, and it’s a big part of a calm adult dog.",
    steps: [
      "Take his mat and a long-lasting chew somewhere quiet first, like a friend’s flat or a quiet café corner.",
      "Sit down, put the mat down and reward him for lying on it, just like at home.",
      "Keep the first visits short, 10–15 minutes, and leave before he gets restless.",
      "Gradually try busier places, like a quiet pub garden in the afternoon.",
      "Sit where he can tuck in out of the way, not in a busy walkway.",
    ],
  },
  {
    id: 'close',
    name: "Close (walk at heel)",
    stage: 4,
    fromWeeks: 24,
    cueId: 'heel',
    summary: "Walk tight by your left leg for short stretches.",
    why: "Useful for passing livestock, other dogs, narrow paths and busy pavements, when a loose lead isn’t tight enough control.",
    steps: [
      "Hold treats in your left hand at your hip. Reward him for being right next to your leg.",
      "Take one step, reward. Two steps, reward. Build up slowly.",
      "Add “Close” as you start walking.",
      "Practise turns and changes of pace.",
      "Use it for short stretches only; let him go back to a relaxed loose lead with “Free”.",
    ],
  },
  {
    id: 'stop-whistle',
    name: "Stop whistle",
    stage: 4,
    fromWeeks: 26,
    cueId: 'stop',
    summary: "One long whistle means stop and sit, wherever you are.",
    why: "An emergency stop at a distance can stop him running towards a road, sheep or a cyclist, when calling him back might take him across the danger.",
    steps: [
      "Close to you, ask for a sit and blow one long peep at the same time. Reward.",
      "Repeat until he sits to the whistle alone.",
      "Try it when he’s a step or two away, then further, on a long line.",
      "Go to him and reward him where he sits, so he learns to stay put.",
      "Practise in lots of places, but not so often that it becomes boring.",
    ],
    cocker: "The stop whistle is a standard part of gundog training and suits a cocker perfectly. Raise your hand at the same time; it’s easy to see from a distance.",
  },
];
